import { setSelectedNavItem } from '../../../redux/actions'
import store from '../../../redux/store'
import NavigationService from '../../../utils/NavigationService'
import { DV_TAB_ASSISTANT_CUSTOMIZATIONS } from '../../../utils/TabNavigationConstants'
import ProjectHelper from '../../SettingsView/ProjectsSettings/ProjectHelper'
import { DEFAULT_ALLOWED_TOOLS } from '../../AssistantDetailedView/Customizations/ToolsAccess/toolOptions'
import { PROJECT_DATA_ASSISTANTS, requestProjectDataOnLookupMiss } from '../../../utils/InitialLoad/projectDataLoader'

export const TYPE_PROMPT_BASED = 'TYPE_PROMPT_BASED'
export const TYPE_3RD_PARTY = 'TYPE_3RD_PARTY'

export const MODEL_GPT3_5 = 'MODEL_GPT3_5'
export const MODEL_GPT4 = 'MODEL_GPT4'
export const MODEL_GPT4O = 'MODEL_GPT4O'
export const MODEL_GPT5_1 = 'MODEL_GPT5_1'
export const MODEL_GPT5_5 = 'MODEL_GPT5_5'
export const MODEL_GPT6_SOL = 'MODEL_GPT6_SOL'
export const MODEL_GPT5_6_TERRA = 'MODEL_GPT5_6_TERRA'
export const MODEL_GPT6_LUNA = 'MODEL_GPT6_LUNA'
export const MODEL_GPT5_2 = 'MODEL_GPT5_2'
export const MODEL_SONAR = 'MODEL_SONAR'
export const MODEL_SONAR_PRO = 'MODEL_SONAR_PRO'
export const MODEL_SONAR_REASONING = 'MODEL_SONAR_REASONING'
export const MODEL_SONAR_REASONING_PRO = 'MODEL_SONAR_REASONING_PRO'
export const MODEL_SONAR_DEEP_RESEARCH = 'MODEL_SONAR_DEEP_RESEARCH'
// Served through OpenRouter rather than OpenAI. The client never needs to know that — provider
// routing is resolved server-side in `functions/Assistant/assistantModelRouting.js`.
export const MODEL_DEEPSEEK_V4_FLASH = 'MODEL_DEEPSEEK_V4_FLASH'

export const TEMPERATURE_VERY_LOW = 'TEMPERATURE_VERY_LOW'
export const TEMPERATURE_LOW = 'TEMPERATURE_LOW'
export const TEMPERATURE_NORMAL = 'TEMPERATURE_NORMAL'
export const TEMPERATURE_HIGH = 'TEMPERATURE_HIGH'
export const TEMPERATURE_VERY_HIGH = 'TEMPERATURE_VERY_HIGH'

export const GLOBAL_PROJECT_ID = 'globalProject'

export const DEFAULT_EMAIL_SIGNATURE = '---\nAnna Alldone\nAI Chief of Staff\nhttps://alldone.app/'

export function getNewDefaultAssistant() {
    const { loggedUser } = store.getState()
    return {
        displayName: '',
        lastEditorId: loggedUser.uid,
        lastEditionDate: Date.now(),
        creatorId: loggedUser.uid,
        createdDate: Date.now(),
        photoURL: '',
        photoURL50: '',
        photoURL300: '',
        description: '',
        prompt: '',
        thirdPartLink: '',
        type: TYPE_PROMPT_BASED,
        instructions: '',
        model: MODEL_GPT6_SOL,
        heartbeatModel: MODEL_GPT6_SOL,
        reasoningEffort: null,
        temperature: TEMPERATURE_NORMAL,
        realtimeVoice: 'marin',
        emailSignature: DEFAULT_EMAIL_SIGNATURE,
        allowedTools: [...DEFAULT_ALLOWED_TOOLS],
        enabledSkillIds: [],
        delegationToolDescriptionManual: '',
        delegationToolDescriptionGenerated: '',
        delegationToolDescriptionGeneratedAt: null,
        delegationToolDescriptionInputHash: '',
        lastVisitBoard: {},
        fromTemplate: false,
        isDefault: false,
        noteIdsByProject: {},
        commentsData: null,
        heartbeatAwakeStart: 28800000,
        heartbeatAwakeEnd: 79200000,
        heartbeatIntervalMs: 1800000,
        heartbeatChancePercent: 0,
        heartbeatChanceNoReplyPercent: 0,
        heartbeatSendWhatsApp: false,
        heartbeatPrompt:
            'Check the done tasks today, comment on it and/or the chat history with one sentence and ask the user if he already did the focus task (remind him) or if there are any other ways you can help.',
    }
}

export const isGlobalAssistant = assistantId => {
    const { globalAssistants } = store.getState()
    return globalAssistants.some(assistant => assistant.uid === assistantId)
}

export const getGlobalAssistant = assistantId => {
    const { globalAssistants } = store.getState()

    for (let i = 0; i < globalAssistants.length; i++) {
        if (globalAssistants[i].uid === assistantId) return globalAssistants[i]
    }

    return null
}

const getGlobalAssistantInProject = (projectId, assistantId) => {
    const project = ProjectHelper.getProjectById(projectId)
    return project && project.globalAssistantIds.includes(assistantId) ? getGlobalAssistant(assistantId) : null
}

const getNormalAssistant = assistantId => {
    const { projectAssistants } = store.getState()

    const assistantsByProject = Object.keys(projectAssistants)

    for (let i = 0; i < assistantsByProject.length; i++) {
        const assistant = getNormalAssistantInProject(assistantsByProject[i], assistantId, false)
        if (assistant) return assistant
    }

    return null
}

export const openAssistantDv = (projectId, assistant) => {
    NavigationService.navigate('AssistantDetailedView', {
        assistantId: assistant.uid,
        assistant,
        projectId,
    })
    store.dispatch(setSelectedNavItem(DV_TAB_ASSISTANT_CUSTOMIZATIONS))
}

const getNormalAssistantInProject = (projectId, assistantId, reportMiss = true) => {
    const { projectAssistants } = store.getState()

    const assistants = projectAssistants[projectId] || []

    for (let n = 0; n < assistants.length; n++) {
        if (assistants[n].uid === assistantId) return assistants[n]
    }

    // AT-2386: project assistants are no longer loaded for every project at login, so a miss can
    // mean "not requested yet" rather than "no such assistant". Reported from this single lookup
    // funnel; the caller keeps its existing null contract and the answer improves on re-render.
    //
    // `reportMiss` exists for `getNormalAssistant`, which SWEEPS every project looking for one id.
    // Every project it walks past is a miss by construction, so reporting from inside that loop
    // would arm every project's assistant watcher the first time any id failed to resolve - the
    // exact eager fan-out this change removes.
    if (reportMiss) requestProjectDataOnLookupMiss(projectId, PROJECT_DATA_ASSISTANTS)

    return null
}

// Re-exported from a dependency-free module so store-only helpers (and their unit tests) can
// resolve an assistant without pulling in this file's transitive firestore/dotenv imports.
export { getAssistantFromState } from './assistantStateLookup'

export const getAssistant = assistantId => {
    const globalAssistant = getGlobalAssistant(assistantId)
    if (globalAssistant) return globalAssistant

    return getNormalAssistant(assistantId)
}

export const getAssistantInProject = (projectId, assistantId) => {
    const globalAssistant = getGlobalAssistantInProject(projectId, assistantId)
    if (globalAssistant) return globalAssistant

    return getNormalAssistantInProject(projectId, assistantId)
}

const getAssignedAssistantInProjectContext = (projectId, objectAssistantId) => {
    if (!objectAssistantId) return null

    const assistant = getAssistantInProject(projectId, objectAssistantId)
    if (assistant) return assistant

    // Objects can keep using an assistant from the user's default project even when they
    // live in another project.
    const { loggedUser } = store.getState()
    const defaultProjectId = loggedUser?.defaultProjectId
    if (defaultProjectId && defaultProjectId !== projectId) {
        const defaultProjectAssistant = getAssistantInProject(defaultProjectId, objectAssistantId)
        if (defaultProjectAssistant) return defaultProjectAssistant
    }

    return null
}

export const getAssistantInProjectObject = (projectId, objectAssistantId) => {
    return (
        getAssignedAssistantInProjectContext(projectId, objectAssistantId) ||
        getDefaultAssistantInProjectById(projectId)
    )
}

export const getDefaultAssistantInProjectById = projectId => {
    const project = ProjectHelper.getProjectById(projectId)
    return getDefaultAssistantInProject(project)
}

// Resolve the assistant that should automatically work on an object in the given project
// when the user triggers assistant help WITHOUT explicitly choosing one (e.g. the task list
// bot button). Unlike `getDefaultAssistantInProject` — which drives what the assistant picker
// shows by default and therefore prefers the global default — this prefers the task's own
// project so the button "just works" with that project's assistant. Preference order:
//   1. The project's own default assistant: an assistant in that project flagged `isDefault`,
//      otherwise the project's configured `project.assistantId` (project or global assistant).
//   2. The overall/global default assistant (`state.defaultAssistant`, derived from the user's
//      default project) as a fallback.
// Returns the resolved assistant object, or `null` when none can be resolved.
export const resolveDefaultAssistantForProject = projectId => {
    const { projectAssistants, defaultAssistant } = store.getState()

    // 1. The project's own default assistant.
    const assistantsInProject = (projectAssistants && projectAssistants[projectId]) || []
    const flaggedDefault = assistantsInProject.find(assistant => assistant.isDefault)
    if (flaggedDefault) return flaggedDefault

    const project = ProjectHelper.getProjectById(projectId)
    if (project && project.assistantId) {
        const projectAssistant = getAssistantInProject(projectId, project.assistantId)
        if (projectAssistant) return projectAssistant
    }

    // 2. Fallback to the overall/global default assistant.
    if (defaultAssistant && defaultAssistant.uid) return defaultAssistant

    return null
}

// Resolve the assistant for a project object without allowing a missing/deleted explicit
// assignment to leak into execution. This is the shared resolution path for task controls:
// keep a valid object assignment, otherwise use the project's assistant and finally the
// account default via `resolveDefaultAssistantForProject`.
export const resolveAssistantForProjectObject = (projectId, objectAssistantId) => {
    return (
        getAssignedAssistantInProjectContext(projectId, objectAssistantId) ||
        resolveDefaultAssistantForProject(projectId)
    )
}

const getDefaultAssistantInProject = project => {
    // Always default to the default assistant from the user's default project, not the
    // per-project assistant. The project assistant (project.assistantId) is still selectable
    // in the assistant picker, but it should no longer be the one shown by default.
    const { defaultAssistant } = store.getState()
    if (defaultAssistant && defaultAssistant.uid) return defaultAssistant

    // Fallback only when the default project has no assistant at all: use the project's own.
    if (project && project.assistantId) {
        const assistant = getAssistantInProject(project.id, project.assistantId)
        if (assistant) return assistant
    }

    return defaultAssistant
}

export const getAssistantProjectId = (assistantId, currentProjectId) => {
    // First check if it's a global assistant
    if (isGlobalAssistant(assistantId)) {
        return GLOBAL_PROJECT_ID
    }

    // Check if assistant exists in the current project
    const assistantInCurrentProject = getNormalAssistantInProject(currentProjectId, assistantId)
    if (assistantInCurrentProject) {
        return currentProjectId
    }

    // If not found in current project, check if we're using default project's assistant
    const currentProject = ProjectHelper.getProjectById(currentProjectId)
    const { defaultProjectId } = store.getState()

    if (currentProject && !currentProject.assistantId && defaultProjectId && currentProjectId !== defaultProjectId) {
        // Check if assistant exists in default project
        const assistantInDefaultProject = getNormalAssistantInProject(defaultProjectId, assistantId)
        if (assistantInDefaultProject) {
            return defaultProjectId
        }
    }

    // Fallback: search all projects
    const { projectAssistants } = store.getState()
    const assistantsByProject = Object.keys(projectAssistants)

    for (let i = 0; i < assistantsByProject.length; i++) {
        const assistant = getNormalAssistantInProject(assistantsByProject[i], assistantId, false)
        if (assistant) return assistantsByProject[i]
    }

    // If still not found, return current project as fallback
    return currentProjectId
}
